import * as React from 'react'
import * as styles from './IntermissionHero.module.scss'
import { IntermissionHeroCard } from './IntermissionHeroCard/IntermissionHeroCard'
import Header from '../FacetedCarousel/Header/Header'
import { CollectionProps } from '../Collection'
import { VideoContext } from '../../../../../DVWebNodeDraperPlayer/videoContext';
import InView from 'react-intersection-observer'
import { StorefrontItem, WPSClient, Args, withArgs, EnrichItemMetadataOutput, WatchlistAction, EnrichmentRequest, ItemPrerolls } from '@utils'
import { stringifyEnrichmentsOnClient } from '../../../../../utils/EnrichItemMetadataUtils'
import { logError } from '../../../utils/csm'
import { NavigationButton } from './Buttons/NavigationButton'

interface IntermissionHeroItem extends StorefrontItem {
    prerolls: ItemPrerolls // Making this mandatory
    updateWatchlistAction?: (newWatchlistAction: WatchlistAction) => void
}

interface IntermissionHeroState {
    currentItemIndex: number
    isAnimating: boolean
    items: IntermissionHeroItem[]
}

class IntermissionHeroComponent extends React.PureComponent<CollectionProps & Args, IntermissionHeroState> {
    enrichHasStarted: boolean

    /* React lifecycle methods */

    constructor(props: CollectionProps & Args) {
        super(props)
        this.enrichHasStarted = false
        this.state = {
            currentItemIndex: 0,
            isAnimating: false,
            // @ts-ignore: TypeScript doesn't pick up that itemPrerolls must now exist
            items: this.props.items.filter((item) => { return Boolean(item.prerolls) })
        }
    }

    /* Navigation */

    move(offset: number) {
        const newIndex = this.state.currentItemIndex + offset
        if (newIndex >= this.state.items.length || newIndex < 0) {
            return
        }
        this.setState({isAnimating: true}, () => { // 1. set opacity to zero
            setTimeout( // 2. allow time for css transition
                () => this.setState({
                    // 3. update metadata, defaulting to first item if out of bounds
                    currentItemIndex: newIndex  
                }, () => {
                    this.setState({isAnimating: false}) // 4. set opacity to one in separate render
                }), 300
            )
        })
    }

    previous = () => {
        this.move(-1)
    }

    next = () => {
        this.move(1)
    }

    /* Listeners */

    onInViewChange = (inView: boolean) => {
        if (inView && !this.enrichHasStarted) {
            this.enrichHasStarted = true
            this.enrich()
        }
    }

    /* Enrichment */

    enrich() {
        this.fetchEnrichments()
            .then(enrichments => this.applyEnrichments(enrichments))
            .catch(error => {
                logError(error, 'Intermission Hero failed to fetch enrichments [playback & watchlist]')
            })
    }

    fetchEnrichments(): Promise<EnrichItemMetadataOutput> {
        const stringifiedRequest = stringifyEnrichmentsOnClient(this.state.items.flatMap(this.createEnrichmentRequestForItem))
        if (!stringifiedRequest) return Promise.reject("Enrichments stringifying failed")
        const wps = new WPSClient()
        return wps.EnrichItemMetadata({
           context: this.props.context,
           itemsToEnrich: stringifiedRequest
        })
    }

    createEnrichmentRequestForItem(item: StorefrontItem): EnrichmentRequest[] {
        if (!item.titleID) return []
        return [{
            titleID: item.titleID,
            trailer: false,
            playback: true,
            preroll: false,
            watchlist: true
        }]
    }

    applyEnrichments(response: EnrichItemMetadataOutput) {

        const newItems = this.state.items.map(item => {
            if (!item.titleID) return item
            const enrichmentsForItem = response.enrichments[item.titleID]
            if (!enrichmentsForItem) return item
            return {
                ...item,
                ...enrichmentsForItem,
                updateWatchlistAction: (newWatchlistAction: WatchlistAction) => {
                    this.setState({
                        items: 
                            this.state.items.map(innerItem => {
                                if (innerItem.titleID !== item.titleID) { return innerItem }
                                else return {...innerItem, watchlistAction: newWatchlistAction}
                            })
                    })
                }
            }
        })

        return new Promise((res, _rej) => {
            this.setState({items: newItems}, res)
        })
    }


    /* Rendering */

    render() {
        if (!this.props.items.length) return

        return <>
            <Header {...this.props} />
            <VideoContext.Consumer>
                {(videoContext) => 
                    <InView
                        onChange={this.onInViewChange}
                        threshold={0.5}
                    >{(inView) => 
                        <div className={styles.container}>
                            <NavigationButton
                                direction={'prev'}
                                onClick={this.previous}
                                active={Boolean(this.state.currentItemIndex)}
                            />     
                            <IntermissionHeroCard
                                {...this.state.items[this.state.currentItemIndex]}
                                className={styles.hero}
                                isAnimating={this.state.isAnimating}
                                inView={inView}
                                videoContext={videoContext}
                                isLastItem={this.state.currentItemIndex === this.state.items.length - 1}
                                moveToNextItem={this.next}
                            />
                            <NavigationButton
                                direction={'next'}
                                onClick={this.next}
                                active={this.state.currentItemIndex < (this.state.items.length - 1)}
                            />   
                        </div>    
                    }     
                    </InView>
                }
            </VideoContext.Consumer>
        </>
    }
}

export const IntermissionHero = withArgs(IntermissionHeroComponent) as React.ComponentType<CollectionProps>